import moment from "moment";
import { GROUPS, DEFAULTS, COMPARES } from "./constants";
import useQueryParams, { useSetMultipleQueryParams } from "../hooks/useQueryParams";

import type { Moment } from "moment";
import { createContext, useCallback, useContext, useMemo } from "react";

interface DatesContextType {
  group: "day" | "week" | "month";
  period: string;
  compare: string;

  setGroup: (group: string) => void;
  setPeriod: (period: string) => void;
  setCompare: (compare: string) => void;

  start: Moment;
  end: Moment;
  preStart: Moment;
  preEnd: Moment;

  setStart: (start: Moment) => void;
  setEnd: (end: Moment) => void;
  setPreStart: (preStart: Moment) => void;
  setPreEnd: (preEnd: Moment) => void;

  includeCurrentPeriod: boolean;
  setIncludeCurrentPeriod: (value: boolean) => void;
}

export const DatesContext = createContext({
  // Context is set in the provider, this should be safe in all cases
} as DatesContextType);

interface Props {
  children: React.ReactNode;
  defaults?: {
    group?: string | null;
    period?: string | null;
    compare?: string | null;
    start?: string | null;
    end?: string | null;
  };
}

export const DatesProvider = ({ children, defaults }: Props) => {
  const [urlGroup] = useQueryParams("group", defaults?.group);
  const [urlPeriod] = useQueryParams("period", defaults?.period);
  const [urlCompare] = useQueryParams("compare", defaults?.compare);
  const [urlStart, setUrlStart] = useQueryParams("start", defaults?.start);
  const [urlEnd, setUrlEnd] = useQueryParams("end", defaults?.end);
  const [urlPreStart, setUrlPreStart] = useQueryParams("preStart");
  const [urlPreEnd, setUrlPreEnd] = useQueryParams("preEnd");
  const [includeCurrentParam, setIncludeCurrentParam] = useQueryParams("includeCurrent", "true");
  const includeCurrentPeriod = includeCurrentParam !== "false";
  const setIncludeCurrentPeriod = useCallback(
    (value: boolean) => {
      setIncludeCurrentParam(value ? "true" : "false");
    },
    [setIncludeCurrentParam],
  );

  const setMultipleQueryParams = useSetMultipleQueryParams();

  const group = GROUPS.find((g) => g.id === urlGroup) ?? DEFAULTS.group;
  const period =
    group.periods.find((p) => p.id === urlPeriod) ?? group.periods.find((p) => p.id === group.defaultPeriod)!;
  const compare = COMPARES.find((c) => c.id === urlCompare) ?? DEFAULTS.compare;
  const end = useMemo(() => {
    if (urlEnd) return moment(urlEnd);

    const baseEnd =
      "getRelativeEnd" in period && typeof period.getRelativeEnd === "function" ?
        period.getRelativeEnd(includeCurrentPeriod)
      : group.getPeriodEnd(includeCurrentPeriod);

    return baseEnd.clone();
  }, [group, period, urlEnd, includeCurrentPeriod]);

  const start = useMemo(() => {
    if (period.id === "custom") {
      return urlStart ? moment(urlStart) : end.clone();
    }

    if (group.id === "month") {
      const months = "monthCount" in period && typeof period.monthCount === "number" ? period.monthCount : 1;
      return end
        .clone()
        .subtract(months - 1, "months")
        .startOf("month");
    }

    return period.getRelativeStart(end);
  }, [period, urlStart, end, group.id]);

  const { preStart, preEnd } = useMemo(
    () =>
      compare.id === "custom" ?
        {
          preStart: urlPreStart ? moment(urlPreStart) : start.clone(),
          preEnd: urlPreEnd ? moment(urlPreEnd) : end.clone(),
        }
      : group.compares[compare.id].getPreviousPeriod(start, end),
    [compare.id, end, group.compares, start, urlPreEnd, urlPreStart],
  );

  const setGroup = useCallback(
    (group: string) => {
      setMultipleQueryParams({
        group: group === DEFAULTS.group.id ? null : group,
        period: null,
        start: null,
        end: null,
      });
    },
    [setMultipleQueryParams],
  );

  const setPeriod = useCallback(
    (period: string) => {
      let nextPeriod: string | null = period;
      if (nextPeriod === group.defaultPeriod) {
        nextPeriod = null;
      }

      let nextStart: string | null = null;
      let nextEnd: string | null = null;
      if (period === "custom") {
        nextStart = start.format("YYYY-MM-DD");
        nextEnd = end.format("YYYY-MM-DD");
      }

      setMultipleQueryParams({
        period: nextPeriod,
        start: nextStart,
        end: nextEnd,
      });
    },
    [end, group.defaultPeriod, setMultipleQueryParams, start],
  );

  const setCompare = useCallback(
    (compare: string) => {
      let nextCompare: string | null = compare;
      if (compare === DEFAULTS.compare.id) {
        nextCompare = null;
      }

      let nextPreStart: string | null = null;
      let nextPreEnd: string | null = null;
      if (compare === "custom") {
        nextPreStart = preStart.format("YYYY-MM-DD");
        nextPreEnd = preEnd.format("YYYY-MM-DD");
      }

      setMultipleQueryParams({
        compare: nextCompare,
        preStart: nextPreStart,
        preEnd: nextPreEnd,
      });
    },
    [preEnd, preStart, setMultipleQueryParams],
  );

  const contextValue = useMemo(
    () => ({
      group: group.id,
      period: period.id,
      compare: compare.id,

      setGroup,
      setPeriod,
      setCompare,

      start,
      end,
      preStart,
      preEnd,

      setStart: (start: Moment) => setUrlStart(start.format("YYYY-MM-DD")),
      setEnd: (end: Moment) => setUrlEnd(end.format("YYYY-MM-DD")),
      setPreStart: (preStart: Moment) => setUrlPreStart(preStart.format("YYYY-MM-DD")),
      setPreEnd: (preEnd: Moment) => setUrlPreEnd(preEnd.format("YYYY-MM-DD")),

      includeCurrentPeriod,
      setIncludeCurrentPeriod,
    }),
    [
      compare.id,
      end,
      group.id,
      period.id,
      preEnd,
      preStart,
      setCompare,
      setGroup,
      setPeriod,
      setUrlEnd,
      setUrlPreEnd,
      setUrlPreStart,
      setUrlStart,
      start,
      includeCurrentPeriod,
      setIncludeCurrentPeriod,
    ],
  );

  return <DatesContext.Provider value={contextValue}>{children}</DatesContext.Provider>;
};

export const useDates = () => useContext(DatesContext);
